import { Component, effect, inject, untracked } from '@angular/core';
import { GuardsCheckEnd, GuardsCheckStart, NavigationCancel, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PathService } from './core/services/path.service';
import { appInit } from './app.init';
import { CommonModule } from '@angular/common';
import { ConfigService } from './core/services/config.service';
import { AuthService } from './core/services/auth.service';
import { TranslocoService } from '@ngneat/transloco';
import { LoaderComponent } from "./shared/loader/loader.component";
import { Subscription, timer } from 'rxjs';
import { SessionExpirationService } from './core/services/session-expiration.service';
import { TitleService } from './core/services/title.service';
import { InitParamsService } from './core/services/init-params.service';
import { OrderService } from './core/services/order.service';
import { AppService } from './app.service';
import { IframeCommunicationService } from './core/services/iframe-communication.service';
import { ToastComponent } from './shared/toast/toast.component';
import { ThemeService } from './core/services/theme.service';
import { ModalService, ModalName } from './core/services/modal.service';

@Component({
  selector: 'cwo-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, LoaderComponent, ToastComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  pathService = inject(PathService);
  initParamsService = inject(InitParamsService);
  orderService = inject(OrderService);
  appStatus = inject(AppService).appInitStatus;
  configService = inject(ConfigService);
  authService = inject(AuthService);
  translocoService = inject(TranslocoService);
  themeService = inject(ThemeService);
  router = inject(Router);
  sessionExpiration = inject(SessionExpirationService);
  iframeCommunicationService = inject(IframeCommunicationService);
  checkingGuard = false;
  loadingTransloco = true;
  checkingGuardSub?: Subscription;
  titleService = inject(TitleService);
  modalService = inject(ModalService);

  constructor() {
    appInit();
    this.router.events.subscribe(event=> {
      if(event instanceof GuardsCheckStart){
        this.updateCheckingGuard(true);
      } else  if (event instanceof GuardsCheckEnd || event instanceof NavigationCancel) {
        this.updateCheckingGuard(false);
      } else if(event instanceof NavigationEnd) {
        this.titleService.updateTitle();
        
        // Notify iframe parent about navigation changes
        if (this.iframeCommunicationService.isInIframe()) {
          const url = window.location.href.includes('#') ? window.location.href.split('#')[0] : window.location.href;
          this.iframeCommunicationService.notifyNavigation(url);
        }
        
        // scroll to top on navigation end because angular's inmemory scroll restoration is not working as it should
        setTimeout(() => {
          window.scrollTo({
            top: 0,
          });
          this.focusPageStart();
        }, 100);
      }
    });

    this.translocoService.selectTranslateObject('route').subscribe(() => {
      this.loadingTransloco = false;
    });

    effect(() => {
      const supportsSms = this.configService.configState().store?.supportsSmsSubscriptions;
      const appStatus = this.appStatus();

      untracked(() => {
        if (supportsSms) {
          this.translocoService.selectTranslateObject('shared_components.sms_sign_up_information').subscribe(v => {
            this.authService.setSMSDisclaimer(`${v.start} ${v.terms_and_conditions} ${v.and} ${v.privacy_policy}. ${v.msg_and_data_rates_may_apply} ${v.message_frequency}`);
          })
        }

        // update the state of the app here, according to the appstatus
        switch (appStatus) {
          case 'COMPLETE':

          this.checkAndOpenSignUpModal();
            // If running in an iframe, notify the parent window that the app is ready
            if (this.iframeCommunicationService.isInIframe()) {
              const isLatteTheme = this.themeService.isLatteTheme();
              this.iframeCommunicationService.sendMessage('THEME',{
                isLatteTheme: isLatteTheme,
                theme: this.themeService.theme(),
                logo: this.configService.configState().store?.logo,
              });
            }
            break;
          case 'LOADING':
            break;
          case 'ERROR':
            break;
        }
      });
    });
    this.sessionExpiration.startChecking();
  }

  private focusPageStart(): void {
    const heading = document.querySelector('h1') as HTMLElement | null;
    const target = heading ?? (document.getElementById('main-content') as HTMLElement | null);
    if (!target) return;
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus();
  }

  private checkAndOpenSignUpModal(): void {
    const params = this.router.parseUrl(this.router.url).queryParams;
    
    if (params['firstName'] || params['lastName'] || params['email'] || 
        params['phone'] || params['name'] || params['hasAgreed'] ||
        params['subscribed'] || params['smsMarketing'] || params['push'] ||
        params['signUpForLoyalty']) {
      
      setTimeout(() => {
        this.modalService.openModal(ModalName.SignIn, { 
          activeTab: 'Sign Up',
          title: 'Sign Up',
          submitButton: 'Sign Up'
        });
      }, 100);
    }
  }

  updateCheckingGuard(value: boolean){
    if(value && !this.checkingGuardSub){
      // this will prevent a potential flickering effect when the guard check is instant
      this.checkingGuardSub = timer(100).subscribe(()=>{
        this.checkingGuard = value;
      });
    } else {
      this.checkingGuardSub?.unsubscribe();
      this.checkingGuardSub = undefined;
      this.checkingGuard = value;
    }
  }

  reload() {
    location.reload();
  }
}
